/**
 * Environmental Geotech — Flight Data & Analysis  (Step 6 of the 8-step intelligence loop)
 *
 * Lists fixed-wing canopy survey missions, shows AI deforestation / wetland loss /
 * burn-extent findings per selected mission, and provides a real chunked-upload
 * dropzone to ingest RGB orthomosaics, multispectral imagery, and flight logs.
 */
import React, { useState, useEffect } from 'react';
import {
  Microscope, Layers, Camera, FileText, Navigation2, TreePine,
  Info, RefreshCw, Cpu, Upload, CheckCircle,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import ChunkedUpload from '../../components/common/ChunkedUpload';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Mission {
  id: string;
  name: string;
  status: string;
  mission_type: string;
  created_at: string;
  updated_at: string;
  metadata?: {
    ai_analysis_status?: string;
    diagnosis?: string;
    severity?: string;
    vertical?: string;
    platform?: string;
    payload?: string;
    multispectral_payload?: string;
    altitude_m?: number;
    zone_name?: string;
    canopy_loss_ha?: number;
    canopy_loss_pct?: number;
    burn_area_ha?: number;
    wetland_loss_ha?: number;
    telemetry_uploaded?: boolean;
    [key: string]: any;
  };
}

// ─── Accepted file types ──────────────────────────────────────────────────────

const FILE_TYPES = [
  {
    icon: Layers,
    iconColour: 'text-blue-600 dark:text-blue-400',
    label: 'RGB Orthomosaic / Nadir Images',
    formats: 'GeoTIFF / JPEG2000 / folder of JPEG/TIF',
    size: '2 GB – 30 GB',
    required: true,
    description:
      'High-resolution RGB mosaic from Sony RX1R II, DJI Zenmuse P1, or equivalent fixed-wing ' +
      'camera. Primary input for canopy cover classification, deforestation extent mapping, ' +
      'and change-detection against baseline orthomosaic.',
  },
  {
    icon: Camera,
    iconColour: 'text-green-600 dark:text-green-400',
    label: 'Multispectral / Hyperspectral Imagery',
    formats: 'GeoTIFF (multi-band)',
    size: '500 MB – 8 GB',
    required: true,
    description:
      'Calibrated multispectral bands (Blue, Green, Red, Red-Edge, NIR) from Micasense RedEdge-MX ' +
      'or Altum-PT. Required for accurate NDVI, NDWI, and NBR computation; mandatory for carbon ' +
      'credit biomass calculations.',
  },
  {
    icon: Navigation2,
    iconColour: 'text-cyan-600 dark:text-cyan-400',
    label: 'Flight Log / Telemetry',
    formats: 'CSV / KML / GPX',
    size: '< 10 MB',
    required: true,
    description:
      'GPS track with altitude, speed, heading, and timestamp. Used to georeference images ' +
      'and validate full-zone coverage. Fixed-wing missions must show complete transect coverage.',
  },
  {
    icon: FileText,
    iconColour: 'text-purple-600 dark:text-purple-400',
    label: 'Radiometric Calibration Panel Images',
    formats: 'JPEG / TIFF',
    size: '< 50 MB',
    required: false,
    description:
      'Pre- and post-flight images of calibration panel for radiometric correction of ' +
      'multispectral bands. Required for scientifically validated NDVI / biomass calculations.',
  },
  {
    icon: FileText,
    iconColour: 'text-gray-600 dark:text-gray-400',
    label: 'Airspace Clearance / NOTAM',
    formats: 'PDF',
    size: '< 5 MB',
    required: false,
    description:
      'Airspace authorisation, BVLOS permit, and NOTAM reference. Required for legal compliance ' +
      'in nationally protected airspace over forest reserves and national parks.',
  },
];

// ─── Status helpers ───────────────────────────────────────────────────────────

const AI_STATUS_LABEL: Record<string, string> = {
  complete:   'Analysis Complete',
  pending:    'Queued for Analysis',
  processing: 'Processing…',
  failed:     'Analysis Failed',
};

const AI_STATUS_CLS: Record<string, string> = {
  complete:   'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  processing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  failed:     'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

// ─── Component ────────────────────────────────────────────────────────────────

export const EnvFlightAnalysisPage: React.FC = () => {
  const navigate = useNavigate();
  const [missions, setMissions]               = useState<Mission[]>([]);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState<string | null>(null);
  const [success, setSuccess]                 = useState<string | null>(null);
  const [guideOpen, setGuideOpen]             = useState(false);
  const [analysingId, setAnalysingId]         = useState<string | null>(null);
  const [selectedMission, setSelectedMission] = useState<Mission | null>(null);
  const [uploadedSessions, setUploadedSessions] = useState<string[]>([]);
  const [finalising, setFinalising]           = useState(false);
  const [missionSearch, setMissionSearch]     = useState('');
  const [missionFilter, setMissionFilter]     = useState<'all' | 'complete' | 'pending'>('all');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/missions', { params: { limit: 100 } });
      const all: Mission[] = res.data?.data?.missions ?? res.data?.missions ?? [];
      const list = all.filter(
        (m) => m.mission_type === 'environmental_survey' || m.metadata?.vertical === 'environmental'
      );
      setMissions(list);
      if (list.length > 0 && !selectedMission) setSelectedMission(list[0]);
      else if (selectedMission) {
        const refreshed = list.find((m) => m.id === selectedMission.id);
        if (refreshed) setSelectedMission(refreshed);
      }
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load missions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const reanalyse = async (missionId: string) => {
    setAnalysingId(missionId);
    setError(null);
    try {
      await api.post(`/missions/${missionId}/analyze-environmental`);
      setSuccess('AI environmental analysis triggered — findings updated.');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Re-analysis request failed');
    } finally {
      setAnalysingId(null);
    }
  };

  const handleFileUploaded = (sessionId: string) => {
    setUploadedSessions((prev) => [...prev, sessionId]);
    setSuccess(`File sealed — SHA-256 verified. Session ${sessionId.slice(0, 8)}… stored.`);
  };

  const handleFinaliseUpload = async () => {
    if (!selectedMission || uploadedSessions.length === 0) return;
    setFinalising(true);
    setError(null);
    try {
      await api.patch(`/missions/${selectedMission.id}`, {
        status: 'completed',
        metadata: {
          ...selectedMission.metadata,
          telemetry_uploaded: true,
          files_uploaded_at: new Date().toISOString(),
          file_count: uploadedSessions.length,
          upload_session_ids: uploadedSessions,
        },
      });
      setSuccess(`${uploadedSessions.length} file${uploadedSessions.length > 1 ? 's' : ''} ingested — mission ready for AI analysis.`);
      setUploadedSessions([]);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to finalise upload');
    } finally {
      setFinalising(false);
    }
  };

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Microscope className="w-7 h-7 text-green-600" /> Flight Data &amp; Analysis
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Step 6 — Upload canopy imagery and review AI deforestation / wetland loss / burn extent analysis
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setGuideOpen(!guideOpen)}>
            <Info className="w-4 h-4 mr-1" /> {guideOpen ? 'Hide' : 'File Guide'}
          </Button>
          <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      {error   && <Alert type="error"   onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── File-type guidance ── */}
      {guideOpen && (
        <Card>
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
            <Info className="w-4 h-4 text-green-600" /> Fixed-Wing Canopy Survey — Accepted File Types
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {FILE_TYPES.map((ft) => {
              const Icon = ft.icon;
              return (
                <div key={ft.label} className="flex gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                  <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${ft.iconColour}`} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{ft.label}</p>
                      {ft.required
                        ? <span className="px-1.5 py-0.5 text-xs rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">Required</span>
                        : <span className="px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">Optional</span>}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{ft.formats} · {ft.size}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{ft.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center py-14"><Spinner size="lg" /></div>
      ) : missions.length === 0 ? (
        <Card>
          <div className="text-center py-14">
            <TreePine className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No environmental survey missions found</p>
            <p className="text-sm text-gray-400 mt-1">Create a mission in <strong>Survey Mission Planner</strong> first</p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">

          {/* ── Left: mission list (sticky, independently scrollable) ── */}
          <div className="lg:sticky lg:top-4 lg:max-h-[calc(100vh-10rem)] lg:overflow-y-auto lg:pr-1 flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Survey Missions ({missions.length})
              </p>
            </div>
            <input
              type="search"
              placeholder="Search missions…"
              value={missionSearch}
              onChange={(e) => setMissionSearch(e.target.value)}
              className="w-full px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-400"
            />
            <div className="flex gap-1.5 flex-wrap">
              {(['all', 'complete', 'pending'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setMissionFilter(f)}
                  className={`px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors ${
                    missionFilter === f
                      ? 'bg-green-600 border-green-600 text-white'
                      : 'border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-green-400'
                  }`}
                >
                  {f === 'all' ? 'All' : f === 'complete' ? 'Analysed' : 'Pending'}
                </button>
              ))}
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              {missions
                .filter((m) => {
                  const aiStatus = m.metadata?.ai_analysis_status ?? 'pending';
                  const matchSearch = !missionSearch || m.name.toLowerCase().includes(missionSearch.toLowerCase());
                  const matchFilter =
                    missionFilter === 'all' ||
                    (missionFilter === 'complete' && aiStatus === 'complete') ||
                    (missionFilter === 'pending'  && aiStatus !== 'complete');
                  return matchSearch && matchFilter;
                })
                .map((m) => {
                  const aiStatus   = m.metadata?.ai_analysis_status ?? 'pending';
                  const isSelected = selectedMission?.id === m.id;
                  const isDone     = aiStatus === 'complete';
                  return (
                    <button
                      key={m.id}
                      onClick={() => { setSelectedMission(m); setUploadedSessions([]); }}
                      className={[
                        'w-full text-left flex items-center gap-0 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-400',
                        isSelected
                          ? 'bg-green-50 dark:bg-green-950/15'
                          : 'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800/60',
                      ].join(' ')}
                    >
                      <span className={`self-stretch w-0.5 flex-shrink-0 rounded-r ${isSelected ? 'bg-green-500' : 'bg-transparent'}`} />
                      <span className="flex-1 min-w-0 px-3 py-2.5">
                        <span className="flex items-center justify-between gap-1.5 mb-0.5">
                          <span className="text-xs font-semibold text-gray-900 dark:text-white truncate">{m.name}</span>
                          <span className={`flex-shrink-0 w-1.5 h-1.5 rounded-full ${isDone ? 'bg-green-500' : 'bg-amber-400'}`} />
                        </span>
                        <span className="text-[11px] text-gray-400 truncate block">
                          {m.metadata?.zone_name ?? '—'} · {m.metadata?.altitude_m != null ? `${m.metadata.altitude_m}m` : '—'} · {new Date(m.created_at).toLocaleDateString()}
                        </span>
                      </span>
                    </button>
                  );
                })}
              {missions.filter((m) => {
                const aiStatus = m.metadata?.ai_analysis_status ?? 'pending';
                const matchSearch = !missionSearch || m.name.toLowerCase().includes(missionSearch.toLowerCase());
                const matchFilter = missionFilter === 'all' || (missionFilter === 'complete' && aiStatus === 'complete') || (missionFilter === 'pending' && aiStatus !== 'complete');
                return matchSearch && matchFilter;
              }).length === 0 && (
                <p className="px-3 py-4 text-xs text-gray-400 text-center">No missions match</p>
              )}
            </div>
          </div>

          {/* ── Right: mission detail + upload + analysis (independently scrollable) ── */}
          <div className="lg:col-span-2 space-y-5 lg:max-h-[calc(100vh-10rem)] lg:overflow-y-auto lg:pr-1">
            {selectedMission && (
              <Card>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-4 pb-3 border-b border-gray-100 dark:border-gray-800">
                  <div>
                    <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                      <Layers className="w-5 h-5 text-green-600" /> {selectedMission.name}
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Zone: {selectedMission.metadata?.zone_name ?? '—'}
                    </p>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => reanalyse(selectedMission.id)}
                    disabled={analysingId === selectedMission.id}
                  >
                    {analysingId === selectedMission.id
                      ? <><Spinner size="sm" /> Analysing…</>
                      : <><Cpu className="w-4 h-4 mr-1" /> Trigger AI Canopy Analysis</>}
                  </Button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs mb-5">
                  {[
                    { label: 'Platform',        value: selectedMission.metadata?.platform ?? '—' },
                    { label: 'Multispectral',   value: selectedMission.metadata?.multispectral_payload ?? 'Not fitted' },
                    { label: 'Altitude',        value: selectedMission.metadata?.altitude_m != null ? `${selectedMission.metadata.altitude_m} m AGL` : '—' },
                  ].map(({ label, value }) => (
                    <div key={label} className="p-3 bg-gray-50 dark:bg-gray-800/60 rounded-lg">
                      <span className="text-gray-400 block mb-1">{label}</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{value}</span>
                    </div>
                  ))}
                </div>

                {/* AI diagnosis */}
                {selectedMission.metadata?.diagnosis && (
                  <div className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-3 mb-5">
                    <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">AI Canopy Analysis</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{selectedMission.metadata.diagnosis}</p>
                    <div className="flex flex-wrap gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                      {selectedMission.metadata.canopy_loss_ha != null && (
                        <span>Canopy loss: <strong className="text-red-600">{Number(selectedMission.metadata.canopy_loss_ha).toFixed(1)} ha</strong></span>
                      )}
                      {selectedMission.metadata.canopy_loss_pct != null && (
                        <span>Loss %: <strong className="text-red-600">{Number(selectedMission.metadata.canopy_loss_pct).toFixed(1)}%</strong></span>
                      )}
                      {selectedMission.metadata.burn_area_ha != null && (
                        <span>Burn: <strong className="text-orange-600">{Number(selectedMission.metadata.burn_area_ha).toFixed(1)} ha</strong></span>
                      )}
                      {selectedMission.metadata.wetland_loss_ha != null && (
                        <span>Wetland loss: <strong className="text-cyan-600">{Number(selectedMission.metadata.wetland_loss_ha).toFixed(1)} ha</strong></span>
                      )}
                    </div>
                  </div>
                )}

                {/* Upload */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <Upload className="w-3.5 h-3.5" /> Upload Flight Data Package
                  </p>
                  <p className="text-[11px] text-gray-400 mb-3">
                    RGB GeoTIFF, Multispectral GeoTIFF, Flight Log (.CSV / .KML), Calibration images · SHA-256 seal generated per file
                  </p>
                  <ChunkedUpload
                    accept=".tif,.tiff,.jpg,.jpeg,.csv,.kml,.gpx,.pdf"
                    maxSizeMB={10240}
                    onComplete={(sessionId) => handleFileUploaded(sessionId)}
                    onError={(msg) => setError(msg)}
                  />
                  {uploadedSessions.length > 0 && (
                    <div className="mt-3 flex items-center justify-between p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                      <span className="text-xs text-green-800 dark:text-green-200 flex items-center gap-1.5">
                        <CheckCircle className="w-3.5 h-3.5" />
                        {uploadedSessions.length} file{uploadedSessions.length > 1 ? 's' : ''} sealed and ready
                      </span>
                      <Button size="sm" onClick={handleFinaliseUpload} loading={finalising} disabled={finalising}>
                        {finalising ? 'Saving…' : 'Confirm Upload Complete'}
                      </Button>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                  <Button variant="outline" size="sm" onClick={() => navigate(`/missions/${selectedMission.id}`)}>
                    Open Full Mission Record
                  </Button>
                </div>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EnvFlightAnalysisPage;

// Made with Bob
